import {
  type HgSupportedLanguage,
  type TrackingData,
  type TrackingHeaderData,
} from '@smg-real-estate/domus';
import { type GeoLocationType } from '@smg-real-estate/geo-libs/dist/types/geo-location';

import { type PropertyType } from '@/models/iazi/common/enums';
import { trackEvent } from '@/tracking';
import { InteractionEvent } from '@/tracking/types';

export const trackHomePageView = (language: HgSupportedLanguage): void => {
  trackEvent(InteractionEvent.PageView, {
    h_page_type_110: 'market_atlas',
    h_page_subtype_112: 'real_estate_market_map',
    h_content_language_116: language,
  });
};

export const trackFooter = (data: TrackingData): void => {
  const trackingData = {
    h_event_category_006: InteractionEvent.FooterInteraction,
    h_event_action_007: data.eventAction,
    h_event_label_008: data.eventLabel,
    non_interaction: false,
  };
  trackEvent(InteractionEvent.FooterInteraction, trackingData);
};

export const trackHeader = (data: TrackingHeaderData): void => {
  const trackingData = {
    h_event_category_006: InteractionEvent.HeaderInteraction,
    h_event_action_007: data.eventAction,
    h_event_label_008: data.eventLabel,
    h_content_language_116: data.contentLanguage,
  };
  trackEvent(InteractionEvent.HeaderInteraction, trackingData);
};

export const trackPriceTrendYearSelection = (value: number): void => {
  const eventLabel = value < 1 ? '6_months' : `${value}_years`;
  trackEvent(InteractionEvent.MarketMapInteraction, {
    h_event_category_006: InteractionEvent.MarketMapInteraction,
    h_event_action_007: 'price_trend',
    h_event_label_008: value === 1 ? '1_year' : eventLabel,
  });
};

export const trackPriceTrendRoomSelection = (value: number): void => {
  const eventLabel = `${value}_rooms`;
  trackEvent(InteractionEvent.MarketMapInteraction, {
    h_event_category_006: InteractionEvent.MarketMapInteraction,
    h_event_action_007: 'price_trend',
    h_event_label_008: eventLabel,
  });
};

export const trackPropertyTypeSwitch = (propertyType: PropertyType): void => {
  trackEvent(InteractionEvent.MarketMapInteraction, {
    h_event_category_006: InteractionEvent.MarketMapInteraction,
    h_event_action_007: `toggle_to_${propertyType}`,
    h_event_label_008: 'housing_prices_map',
  });
};

export const trackCurrentPriceBoxToValuationEntry = (): void => {
  trackEvent(InteractionEvent.MarketMapInteraction, {
    h_event_category_006: InteractionEvent.MarketMapInteraction,
    h_event_action_007: 'open_valuation_funnel',
    h_event_label_008: 'current_market_price_box',
  });
};

export const trackLocationSearch = (geoId: string, propertyType: PropertyType): void => {
  trackEvent(InteractionEvent.MarketMapInteraction, {
    h_event_category_006: InteractionEvent.MarketMapInteraction,
    h_event_action_007: 'market_map_search',
    h_event_label_008: 'housing_prices_map',
    h_search_term_geo_080: geoId,
    h_search_filter_category_075: propertyType,
    h_search_filter_category_sub_084: '',
  });
};

export const trackMapInteraction = (geoTag: string, geoLevel: GeoLocationType): void => {
  trackEvent(InteractionEvent.MarketMapInteraction, {
    h_event_category_006: InteractionEvent.MarketMapInteraction,
    h_event_action_007: `select_${geoLevel}`,
    h_event_label_008: geoLevel,
    h_search_term_geo_080: geoTag,
  });
};

export const trackValuationBoxCta = (): void => {
  trackEvent(InteractionEvent.MarketMapInteraction, {
    h_event_category_006: InteractionEvent.MarketMapInteraction,
    h_event_action_007: 'open_valuation_funnel',
    h_event_label_008: 'free_valuation_box',
  });
};

export const trackHowBannerIsVisible = (): void => {
  trackEvent(InteractionEvent.FeatureDisplayed, {
    h_event_action_007: 'display_homeowner_banner',
    h_event_label_008: 'market_atlas',
  });
};

export const trackHowBannerCtaClick = (
  action: 'go_to_my_real_estate' | 'go_to_valuation_form',
): void => {
  trackEvent(InteractionEvent.ButtonClick, {
    h_event_action_007: action,
    h_event_label_008: 'market_atlas',
  });
};
